// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator } from "@azure/core-paging";
import type { SimplePollerLike, OperationState } from "@azure/core-lro";
import type {
  ServiceGateway,
  ServiceGatewaysListAllOptionalParams,
  ServiceGatewaysListOptionalParams,
  ServiceGatewayAddressLocationResponse,
  ServiceGatewaysGetAddressLocationsOptionalParams,
  ServiceGatewayService,
  ServiceGatewaysGetServicesOptionalParams,
  ServiceGatewaysDeleteOptionalParams,
  ServiceGatewaysDeleteResponse,
  ServiceGatewaysGetOptionalParams,
  ServiceGatewaysGetResponse,
  ServiceGatewaysCreateOrUpdateOptionalParams,
  ServiceGatewaysCreateOrUpdateResponse,
  TagsObject,
  ServiceGatewaysUpdateTagsOptionalParams,
  ServiceGatewaysUpdateTagsResponse,
  ServiceGatewayUpdateAddressLocationsRequest,
  ServiceGatewaysUpdateAddressLocationsOptionalParams,
  ServiceGatewaysUpdateAddressLocationsResponse,
  ServiceGatewayUpdateServicesRequest,
  ServiceGatewaysUpdateServicesOptionalParams,
  ServiceGatewaysUpdateServicesResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Interface representing a ServiceGateways. */
export interface ServiceGateways {
  /**
   * Gets all the service gateways in a subscription.
   * @param options The options parameters.
   */
  listAll(
    options?: ServiceGatewaysListAllOptionalParams,
  ): PagedAsyncIterableIterator<ServiceGateway>;
  /**
   * Gets all the service gateways in a resource group.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param options The options parameters.
   */
  list(
    resourceGroupName: string,
    options?: ServiceGatewaysListOptionalParams,
  ): PagedAsyncIterableIterator<ServiceGateway>;
  /**
   * Get address locations in service gateway.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param serviceGatewayName The name of the service gateway.
   * @param options The options parameters.
   */
  listAddressLocations(
    resourceGroupName: string,
    serviceGatewayName: string,
    options?: ServiceGatewaysGetAddressLocationsOptionalParams,
  ): PagedAsyncIterableIterator<ServiceGatewayAddressLocationResponse>;
  /**
   * Get Services in service gateway.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param serviceGatewayName The name of the service gateway.
   * @param options The options parameters.
   */
  listServices(
    resourceGroupName: string,
    serviceGatewayName: string,
    options?: ServiceGatewaysGetServicesOptionalParams,
  ): PagedAsyncIterableIterator<ServiceGatewayService>;
  /**
   * Deletes the specified service gateway.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param serviceGatewayName The name of the service gateway.
   * @param options The options parameters.
   */
  beginDelete(
    resourceGroupName: string,
    serviceGatewayName: string,
    options?: ServiceGatewaysDeleteOptionalParams,
  ): Promise<
    SimplePollerLike<OperationState<ServiceGatewaysDeleteResponse>, ServiceGatewaysDeleteResponse>
  >;
  /**
   * Deletes the specified service gateway.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param serviceGatewayName The name of the service gateway.
   * @param options The options parameters.
   */
  beginDeleteAndWait(
    resourceGroupName: string,
    serviceGatewayName: string,
    options?: ServiceGatewaysDeleteOptionalParams,
  ): Promise<ServiceGatewaysDeleteResponse>;
  /**
   * Gets the specified service gateway.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param serviceGatewayName The name of the service gateway.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    serviceGatewayName: string,
    options?: ServiceGatewaysGetOptionalParams,
  ): Promise<ServiceGatewaysGetResponse>;
  /**
   * Creates or updates a service gateway.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param serviceGatewayName The name of the service gateway.
   * @param parameters Parameters supplied to the create or update service gateway operation.
   * @param options The options parameters.
   */
  beginCreateOrUpdate(
    resourceGroupName: string,
    serviceGatewayName: string,
    parameters: ServiceGateway,
    options?: ServiceGatewaysCreateOrUpdateOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<ServiceGatewaysCreateOrUpdateResponse>,
      ServiceGatewaysCreateOrUpdateResponse
    >
  >;
  /**
   * Creates or updates a service gateway.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param serviceGatewayName The name of the service gateway.
   * @param parameters Parameters supplied to the create or update service gateway operation.
   * @param options The options parameters.
   */
  beginCreateOrUpdateAndWait(
    resourceGroupName: string,
    serviceGatewayName: string,
    parameters: ServiceGateway,
    options?: ServiceGatewaysCreateOrUpdateOptionalParams,
  ): Promise<ServiceGatewaysCreateOrUpdateResponse>;
  /**
   * Updates a service gateway tags.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param serviceGatewayName The name of the service gateway.
   * @param parameters Parameters supplied to update service gateway tags.
   * @param options The options parameters.
   */
  updateTags(
    resourceGroupName: string,
    serviceGatewayName: string,
    parameters: TagsObject,
    options?: ServiceGatewaysUpdateTagsOptionalParams,
  ): Promise<ServiceGatewaysUpdateTagsResponse>;
  /**
   * Creates or updates address locations within the service gateway.
   *
   * The request supports both full and partial update modes at two levels: location and address.
   *
   * Full update replaces all existing data.
   *
   * Partial update modifies only the specified entries:
   *
   * For location-level partial updates, if no address is provided, the existing address will be deleted.
   *
   * For address-level partial updates, if no services are provided, the existing services will be
   * considered for deletion.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param serviceGatewayName The name of the service gateway.
   * @param parameters Parameters supplied to the create or updates address locations in service gateway
   *                   operation.
   * @param options The options parameters.
   */
  beginUpdateAddressLocations(
    resourceGroupName: string,
    serviceGatewayName: string,
    parameters: ServiceGatewayUpdateAddressLocationsRequest,
    options?: ServiceGatewaysUpdateAddressLocationsOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<ServiceGatewaysUpdateAddressLocationsResponse>,
      ServiceGatewaysUpdateAddressLocationsResponse
    >
  >;
  /**
   * Creates or updates address locations within the service gateway.
   *
   * The request supports both full and partial update modes at two levels: location and address.
   *
   * Full update replaces all existing data.
   *
   * Partial update modifies only the specified entries:
   *
   * For location-level partial updates, if no address is provided, the existing address will be deleted.
   *
   * For address-level partial updates, if no services are provided, the existing services will be
   * considered for deletion.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param serviceGatewayName The name of the service gateway.
   * @param parameters Parameters supplied to the create or updates address locations in service gateway
   *                   operation.
   * @param options The options parameters.
   */
  beginUpdateAddressLocationsAndWait(
    resourceGroupName: string,
    serviceGatewayName: string,
    parameters: ServiceGatewayUpdateAddressLocationsRequest,
    options?: ServiceGatewaysUpdateAddressLocationsOptionalParams,
  ): Promise<ServiceGatewaysUpdateAddressLocationsResponse>;
  /**
   * Creates, updates, or deletes services within the service gateway.
   * The request supports both full and partial update modes at the service level.
   *
   * Full update replaces all existing services with the new list provided in the request.
   * Partial update modifies only the specified services.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param serviceGatewayName The name of the service gateway.
   * @param parameters Parameters supplied to the create or updates services in service gateway
   *                   operation.
   * @param options The options parameters.
   */
  beginUpdateServices(
    resourceGroupName: string,
    serviceGatewayName: string,
    parameters: ServiceGatewayUpdateServicesRequest,
    options?: ServiceGatewaysUpdateServicesOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<ServiceGatewaysUpdateServicesResponse>,
      ServiceGatewaysUpdateServicesResponse
    >
  >;
  /**
   * Creates, updates, or deletes services within the service gateway.
   * The request supports both full and partial update modes at the service level.
   *
   * Full update replaces all existing services with the new list provided in the request.
   * Partial update modifies only the specified services.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param serviceGatewayName The name of the service gateway.
   * @param parameters Parameters supplied to the create or updates services in service gateway
   *                   operation.
   * @param options The options parameters.
   */
  beginUpdateServicesAndWait(
    resourceGroupName: string,
    serviceGatewayName: string,
    parameters: ServiceGatewayUpdateServicesRequest,
    options?: ServiceGatewaysUpdateServicesOptionalParams,
  ): Promise<ServiceGatewaysUpdateServicesResponse>;
}
